import { IAuthRepository } from '@/core/domain/repositories/IAuthRepository'
import { User, AuthSession } from '@/core/domain/entities/User'
import { Result, createOk, createErr } from '@/core/shared/utils/Result'
import { HttpClient } from '@/infrastructure/http/axiosClient'
import { useSessionStore } from '@/core/stores'

export interface AuthResponse {
  user: User
  accessToken: string
  refreshToken?: string
  expiresAt: string
}

export interface CheckAuthResponse {
  success: boolean
  data: {
    uid: string
    type: string
  }
}

export class AxiosAuthRepository implements IAuthRepository {
  constructor(private readonly httpClient: HttpClient) {}

  async checkAuth(token: string): Promise<Result<User, Error>> {
    try {
      const response = await this.httpClient.get<CheckAuthResponse>(
        '/check-auth',
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )

      if (!response.success) {
        return createErr(new Error('Authentication failed'))
      }

      const user: User = {
        id: response.data.uid,
        type: response.data.type,
        token,
        isAuthenticated: true,
      }

      return createOk(user)
    } catch (error) {
      console.error('Failed to check auth:', error)
      return createErr(
        error instanceof Error ? error : new Error('Auth check failed')
      )
    }
  }

  async getCurrentSession(): Promise<Result<AuthSession | null, Error>> {
    try {
      const session = useSessionStore.getState().getSession()
      return createOk(session)
    } catch (error) {
      console.error('Failed to get current session:', error)
      return createErr(
        error instanceof Error ? error : new Error('Failed to get session')
      )
    }
  }

  async storeSession(session: AuthSession): Promise<Result<void, Error>> {
    try {
      useSessionStore.getState().setSession(session)
      return createOk(undefined)
    } catch (error) {
      console.error('Failed to store session:', error)
      return createErr(
        error instanceof Error ? error : new Error('Failed to store session')
      )
    }
  }

  async clearSession(): Promise<Result<void, Error>> {
    try {
      useSessionStore.getState().clearSession()
      return createOk(undefined)
    } catch (error) {
      console.error('Failed to clear session:', error)
      return createErr(
        error instanceof Error ? error : new Error('Failed to clear session')
      )
    }
  }
}
